/**
 * Centralized utility for status filter conversion and handling
 *
 * This utility standardizes the conversion between UI status representation
 * and backend is_active boolean values across the entire application.
 */

export type StatusFilterValue = 'all' | 'active' | 'inactive' | 'true' | 'false';

export interface FilterState {
  status?: StatusFilterValue;
  is_active?: boolean;
  [key: string]: any;
}

/**
 * Converts UI status string to backend is_active boolean
 *
 * @param status - Status value from UI components
 * @returns boolean value for backend API or undefined for "all"
 */
export function statusToIsActive(status: StatusFilterValue | undefined): boolean | undefined {
  if (!status || status === 'all') {
    return undefined; // Show all products
  }

  if (status === 'active' || status === 'true') {
    return true;
  }

  if (status === 'inactive' || status === 'false') {
    return false;
  }

  return undefined; // Fallback to show all
}

/**
 * Converts backend is_active boolean to UI status string
 *
 * @param isActive - Boolean value from backend or filter state
 * @returns Status string for UI components
 */
export function isActiveToStatus(isActive: boolean | undefined): StatusFilterValue {
  if (isActive === undefined || isActive === null) {
    return 'all';
  }

  return isActive ? 'true' : 'false';
}

/**
 * Gets the current status value for UI display, handling both status and is_active
 *
 * @param filters - Current filter state object
 * @returns Status string for Select component value
 */
export function getCurrentStatusValue(filters: any): StatusFilterValue {
  // Handle null/undefined filters gracefully
  if (!filters) {
    return 'all';
  }

  // Priority 1: If status is explicitly set to 'all', return it
  if (filters.status === 'all') {
    return 'all';
  }

  // Priority 2: If status is explicitly set and not 'all', use it
  if (filters.status && filters.status !== 'all') {
    // Validate that it's a valid status value
    if (isValidStatusValue(filters.status)) {
      return filters.status as StatusFilterValue;
    }
  }

  // Priority 3: If is_active is explicitly set as boolean, convert it to status string
  if (filters.is_active !== undefined && filters.is_active !== null) {
    return isActiveToStatus(filters.is_active);
  }

  // Default: Show all statuses
  return 'all';
}

/**
 * Creates the proper filter update based on status change
 *
 * @param newStatus - New status value from UI
 * @param currentFilters - Current filter state
 * @returns Updated filter object with proper status/is_active values
 */
export function createStatusFilterUpdate(
  newStatus: StatusFilterValue,
  currentFilters: FilterState
): Partial<FilterState> {
  const normalizedStatus = newStatus === undefined || newStatus === null ? 'all' : newStatus;

  if (normalizedStatus === 'all') {
    // Remove is_active filter to show all products, keep other filters
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { is_active, status, ...rest } = currentFilters;
    return { ...rest, page: 1 };
  } else {
    // Set is_active boolean for backend filtering
    const booleanValue = statusToIsActive(normalizedStatus);
    return {
      ...currentFilters,
      is_active: booleanValue,
      status: normalizedStatus,
      page: 1,
    };
  }
}

/**
 * Validates if a status filter value is valid
 *
 * @param value - Value to validate
 * @returns true if valid status value
 */
export function isValidStatusValue(value: any): value is StatusFilterValue {
  return ['all', 'active', 'inactive', 'true', 'false'].includes(value);
}

/**
 * Debug helper to log status filter state for troubleshooting
 *
 * @param context - Context where this is being called from
 * @param filters - Current filter state
 * @param action - What action is being performed
 */
export function debugStatusFilter(context: string, filters: FilterState, action?: string): void {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug(`[StatusFilter:${context}]`, {
      action,
      status: filters.status,
      is_active: filters.is_active,
      currentStatusValue: getCurrentStatusValue(filters),
      timestamp: new Date().toISOString(),
    });
  }
}
